import { createContext, useState, useContext, useMemo } from 'react';
import { Alert, AlertColor, Snackbar, Typography } from '@mui/material';

type ErrorContextType = {
  showError: (message: string, severity?: AlertColor) => void;
};

export interface ErrorProviderProps {
  children: React.ReactNode;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
};

export function ErrorProvider({ children }: ErrorProviderProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor | undefined>(undefined);

  const showError = (msg: string, notificationType = 'error' as AlertColor) => {
    setMessage(msg);
    setSeverity(notificationType);
    setOpen(true);
  };

  // ✅ Memoize the context value to avoid re-creating it every render
  const contextValue = useMemo(() => ({ showError }), []);

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={severity}
          variant="filled"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'primaryLight.contrastText',
            'MuiAlert-action': {
              pt: 0,
            },
          }}
        >
          <Typography color="primaryLight.contrastText" fontWeight={600}>
            {message}
          </Typography>
        </Alert>
      </Snackbar>
    </ErrorContext.Provider>
  );
}

export default ErrorProvider;
